<template>
  <VueQuillEditor
    :key="quillKey"
    ref="quillEditorEl"
    v-model="newContent"
    :options="editorOption"
    @focus="$event.enable()"
    @blur="onEditorBlur"
  />
</template>

<script>
import { mapState } from 'vuex'

import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

import { quillEditor as VueQuillEditor } from 'vue-quill-editor'
import Quill from 'quill'
import 'quill-paste-smart'

export default {
  name: 'QuillEditor',
  components: { VueQuillEditor },
  props: {
    content: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState('settings', ['palette']),
    editorOption() {
      return {
        theme: 'bubble',
        placeholder: '',
        modules: {
          toolbar: [
            'bold',
            'italic',
            'underline',
            'strike',
            {
              font: [
                'mulish',
                'roboto',
                'helvetica',
                'lato',
                'nunito',
                'staatliches',
                'lilitaone',
                'quicksand',
                'lora',
                'vollkorn',
              ],
            },
            { size: this.fontSizeOptions },
            { color: ['#000000', '#ffffff', ...this.palette] },
            { background: ['transparent', '#000000', '#ffffff', ...this.palette] },
            { align: ['', 'center', 'right', 'justify'] },
            { link: { attributes: { rel: 'noopener noreferrer' } } },
          ],
          clipboard: {
            allowed: {
              tags: ['a', 'b', 'strong', 'u', 's', 'i', 'p', 'br', 'ul', 'ol', 'li', 'span'],
              attributes: ['href', 'rel', 'target', 'class'],
            },
            keepSelection: true,
            substituteBlockElements: true,
            magicPasteLinks: true,
            hooks: {
              uponSanitizeElement(node, data, config) {
                console.log(node)
              },
            },
          },
        },
      }
    },
    fontSizeOptions() {
      return [
        '12px',
        '16px',
        '20px',
        '24px',
        '28px',
        '32px',
        '36px',
        '40px',
        '44px',
        '48px',
        '52px',
        '56px',
        '60px',
        '64px',
      ]
    },
  },
  data() {
    return {
      quillKey: 0,
      newContent: this.content,
    }
  },
  watch: {
    palette() {
      this.quillKey += 1
      this.preventClose()
    },
    content(newContent) {
      this.quillKey += 1
      this.newContent = newContent
      this.preventClose()
    },
  },
  created() {
    const FontFamily = Quill.import('attributors/class/font')
    const SizeStyle = Quill.import('attributors/style/size')

    FontFamily.whitelist = [
      'roboto',
      'helvetica',
      'lato',
      'mulish',
      'nunito',
      'staatliches',
      'lilitaone',
      'quicksand',
      'lora',
      'vollkorn',
    ]

    delete SizeStyle.whitelist

    Quill.register(FontFamily, true)
    Quill.register(SizeStyle, true)
  },
  mounted() {
    this.preventClose()
  },
  methods: {
    preventClose() {
      this.$nextTick(() => {
        this.$el.querySelector('.ql-toolbar').addEventListener('mousedown', e => {
          e.preventDefault()
        })
      })
    },
    onEditorBlur() {
      if (this.content !== this.newContent) this.$emit('change', this.newContent)
    },
  },
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Lato&family=Lilita+One&family=Lora&family=Mulish&family=Nunito&family=Quicksand&family=Roboto&family=Staatliches&family=Vollkorn&display=swap');

.ql-bubble .ql-tooltip-editor input[type='text'] {
  color: $grey;
}

.ql-bubble .ql-tooltip-editor a.ql-close {
  top: 10px;
  position: absolute;
  right: 20px;
}

.ql-bubble .ql-tooltip-editor a.ql-close:before {
  color: #333;
  content: '\D7';
  font-size: 18px;
  font-weight: bold;
  position: absolute;
  line-height: 1;
  top: 20px;
  background-color: #ebebeb;
  border-radius: 50%;
  display: flex;
  box-sizing: border-box;
  padding: 0;
  width: 20px;
  height: 20px;
  justify-content: center;
  align-items: center;
  visibility: unset;
}

.ql-bubble .ql-tooltip-editor a.ql-close:after {
  all: unset;
}

.ql-bubble .ql-tooltip {
  z-index: 20;
  background: white;
  border-radius: 4px;
  position: fixed;
  top: 60px !important;
  left: 50% !important;
  border: 1px solid $grey;

  button,
  .ql-picker-label,
  .ql-picker-item {
    color: $grey;
    .ql-stroke {
      stroke: $grey !important;
    }
    .ql-fill {
      fill: $grey !important;
    }
    &.ql-active,
    &.ql-selected {
      color: $cherry !important;
      .ql-stroke {
        stroke: $cherry !important;
      }
      .ql-fill {
        fill: $cherry !important;
      }
    }

    &:hover {
      color: $grey-25;
    }
  }

  .ql-formats {
    display: flex;
  }

  .ql-tooltip-arrow {
    display: none;
  }
}

.ql-picker-options {
  background: $white !important;

  .ql-picker-item {
    margin: 2px;
  }
}

.ql-size .ql-picker-options {
  top: unset !important;
  max-height: 200px !important;
  overflow-y: auto !important;
}

.ql-color,
.ql-background {
  .ql-picker-item {
    border: 1px solid $grey !important;

    &[data-value='transparent'] {
      position: relative;
      &::before {
        content: '';
        height: 1px;
        width: 140%;
        position: absolute;
        top: 7px;
        left: -3px;
        transform: rotateZ(-45deg);
        background: red !important;
      }
      &::after {
        content: '';
        height: 1px;
        width: 140%;
        position: absolute;
        top: 7px;
        left: -2px;
        transform: rotateZ(45deg);
        background: red !important;
      }
    }
  }
}

.ql-container {
  font-size: 1rem;
}

@for $i from 1 through 16 {
  .ql-picker-item[data-value='#{$i * 4}px']::before,
  .ql-picker-label[data-value='#{$i * 4}px']::before {
    content: '#{$i * 4}px' !important;
  }
}

.ql-picker-item[data-value='roboto']::before,
.ql-picker-label[data-value='roboto']::before {
  content: 'Roboto' !important;
  font-family: 'Roboto', sans-serif !important;
}

.ql-picker-item[data-value='helvetica']::before,
.ql-picker-label[data-value='helvetica']::before {
  content: 'Helvetica' !important;
  font-family: 'Helvetica', sans-serif !important;
}

.ql-picker-item[data-value='lilitaone']::before,
.ql-picker-label[data-value='lilitaone']::before {
  content: 'Lilita One' !important;
  font-family: 'Lilita One', cursive !important;
}

.ql-picker-item[data-value='lato']::before,
.ql-picker-label[data-value='lato']::before {
  content: 'Lato' !important;
  font-family: 'Lato', sans-serif !important;
}

.ql-picker-item[data-value='mulish']::before,
.ql-picker-label[data-value='mulish']::before {
  content: 'Mulish' !important;
  font-family: 'Mulish', sans-serif !important;
}

.ql-picker-item[data-value='nunito']::before,
.ql-picker-label[data-value='nunito']::before {
  content: 'Nunito' !important;
  font-family: 'Nunito', sans-serif !important;
}

.ql-picker-item[data-value='staatliches']::before,
.ql-picker-label[data-value='staatliches']::before {
  content: 'Staatliches' !important;
  font-family: 'Staatliches', cursive !important;
}

.ql-picker-item[data-value='quicksand']::before,
.ql-picker-label[data-value='quicksand']::before {
  content: 'Quicksand' !important;
  font-family: 'Quicksand', sans-serif !important;
}

.ql-picker-item[data-value='lora']::before,
.ql-picker-label[data-value='lora']::before {
  content: 'Lora' !important;
  font-family: 'Lora', serif !important;
}

.ql-picker-item[data-value='vollkorn']::before,
.ql-picker-label[data-value='vollkorn']::before {
  content: 'Vollkorn' !important;
  font-family: 'Vollkorn', serif !important;
}

.ql-font-roboto {
  font-family: 'Roboto', sans-serif !important;
}

.ql-font-helvetica {
  font-family: 'Helvetica', sans-serif !important;
}

.ql-font-lilitaone {
  font-family: 'Lilita One', cursive !important;
}

.ql-font-lato {
  font-family: 'Lato', sans-serif !important;
}

.ql-font-mulish {
  font-family: 'Mulish', sans-serif !important;
}

.ql-font-nunito {
  font-family: 'Nunito', sans-serif !important;
}

.ql-font-staatliches {
  font-family: 'Staatliches', cursive !important;
}

.ql-font-quicksand {
  font-family: 'Quicksand', sans-serif !important;
}

.ql-font-lora {
  font-family: 'Lora', serif !important;
}

.ql-font-vollkorn {
  font-family: 'Vollkorn', serif !important;
}
</style>
